import { LabelVariant } from '@workwhile/ui'

// Mirrors the backend `JetAlertType` enum (lib/enum_definitions.py). Each value
// is a recurring JET condition jet-alert pages about until a human resolves it.
export const JET_ALERT_TYPE_VALUES = [
  'surplus_shift',
  'filled_missing_assignment',
  'rejected_group',
  'missing_timezone_group',
  'unmapped_location',
  'unmapped_position',
] as const

export type JetAlertType = (typeof JET_ALERT_TYPE_VALUES)[number]

export const JET_ALERT_TYPE_LABELS: Record<JetAlertType, string> = {
  surplus_shift: 'Surplus shift',
  filled_missing_assignment: 'Filled missing assignment',
  rejected_group: 'Rejected group',
  missing_timezone_group: 'Missing timezone group',
  unmapped_location: 'Unmapped location',
  unmapped_position: 'Unmapped position',
}

// A short hint describing what the row's `key` column refers to, per type.
export const JET_ALERT_TYPE_KEY_HINTS: Record<JetAlertType, string> = {
  surplus_shift: 'Shift ID',
  filled_missing_assignment: 'Assignment ID',
  rejected_group: 'Group key',
  missing_timezone_group: 'Group key',
  unmapped_location: 'JET store ID',
  unmapped_position: 'JET position ID',
}

export const JET_ALERT_TYPE_LABEL_VARIANTS: Record<JetAlertType, LabelVariant> =
  {
    surplus_shift: 'warning',
    filled_missing_assignment: 'error',
    rejected_group: 'error',
    missing_timezone_group: 'warning',
    unmapped_location: 'neutral',
    unmapped_position: 'neutral',
  }

export const JET_ALERT_TYPE_OPTIONS: {
  value: JetAlertType
  label: string
}[] = JET_ALERT_TYPE_VALUES.map((value) => ({
  value,
  label: JET_ALERT_TYPE_LABELS[value],
}))

// Lifecycle filter values: an alert is `active` while `resolvedAt` is null.
export const JET_ALERT_STATUS_VALUES = ['active', 'resolved'] as const

export type JetAlertStatus = (typeof JET_ALERT_STATUS_VALUES)[number]

export const JET_ALERT_STATUS_LABELS: Record<JetAlertStatus, string> = {
  active: 'Active',
  resolved: 'Resolved',
}

export const JET_ALERT_STATUS_LABEL_VARIANTS: Record<
  JetAlertStatus,
  LabelVariant
> = {
  active: 'error',
  resolved: 'success',
}

export const JET_ALERT_STATUS_OPTIONS: {
  value: JetAlertStatus
  label: string
}[] = JET_ALERT_STATUS_VALUES.map((value) => ({
  value,
  label: JET_ALERT_STATUS_LABELS[value],
}))

// Ordering controls. `dateField` selects which timestamp the `days ago` window
// filters against; `sortBy` selects the ordering column (server-side).
export const JET_ALERT_DATE_FIELD_VALUES = [
  'created_at',
  'last_alerted_at',
] as const

export type JetAlertDateField = (typeof JET_ALERT_DATE_FIELD_VALUES)[number]

export const JET_ALERT_DATE_FIELD_LABELS: Record<JetAlertDateField, string> = {
  created_at: 'Created at',
  last_alerted_at: 'Last alerted at',
}

export const JET_ALERT_SORT_BY_VALUES = [
  'created_at',
  'last_alerted_at',
  'resolved_at',
  'alert_type',
] as const

export type JetAlertSortBy = (typeof JET_ALERT_SORT_BY_VALUES)[number]

export const JET_ALERT_SORT_BY_LABELS: Record<JetAlertSortBy, string> = {
  created_at: 'Created at',
  last_alerted_at: 'Last alerted at',
  resolved_at: 'Resolved at',
  alert_type: 'Alert type',
}

export const JET_ALERT_SORT_DIR_VALUES = ['asc', 'desc'] as const

export type JetAlertSortDir = (typeof JET_ALERT_SORT_DIR_VALUES)[number]
